import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const transformations = [
  {
    id: 1,
    title: 'Abattage d\'arbre - Élagage professionnel',
    before: 'https://customer-assets.emergentagent.com/job_2513ce8b-8bd6-4c11-809d-00965abc9d4a/artifacts/2wgyedoq_IMG_1091.png',
    after: 'https://customer-assets.emergentagent.com/job_2513ce8b-8bd6-4c11-809d-00965abc9d4a/artifacts/utkg34rp_IMG_1096.png',
    description: 'Abattage et évacuation de souches et arbres',
    useContain: false
  },
  {
    id: 2,
    title: 'Entretien de jardin - Transformation complète',
    before: 'https://customer-assets.emergentagent.com/job_concierge-website/artifacts/e76e9xzc_jardin%20avant.png',
    after: 'https://customer-assets.emergentagent.com/job_concierge-website/artifacts/9gfvarmj_jardin%20apre%CC%80s.png',
    description: 'Aménagement et entretien de vos espaces verts',
    useContain: false
  },
  {
    id: 3,
    title: 'Nettoyage de mobilier',
    before: 'https://customer-assets.emergentagent.com/job_concierge-website/artifacts/x7d0dt9b_chaise%20avant.png',
    after: 'https://customer-assets.emergentagent.com/job_concierge-website/artifacts/pt5tfw6i_chaise%20apre%CC%80s.png',
    description: 'Remise à neuf de votre mobilier',
    useContain: false
  },
  {
    id: 4,
    title: 'Nettoyage intérieur véhicule',
    before: 'https://customer-assets.emergentagent.com/job_concierge-website/artifacts/5wzaouh4_pdal%20avnt.png',
    after: 'https://customer-assets.emergentagent.com/job_concierge-website/artifacts/77i10w5a_Capture%20d%E2%80%99e%CC%81cran%202026-01-22%20a%CC%80%2010.07.45.png',
    description: 'Nettoyage en profondeur des pédales et habitacle',
    useContain: false
  },
  {
    id: 5,
    title: 'Nettoyage extérieur véhicule',
    before: 'https://customer-assets.emergentagent.com/job_ef56100f-4301-4e45-8a7c-67ecba0b3653/artifacts/bskktfm3_jantes%20sales.jpg',
    after: 'https://customer-assets.emergentagent.com/job_ef56100f-4301-4e45-8a7c-67ecba0b3653/artifacts/phr23jho_jantes%20propre.jpg',
    description: 'Nettoyage minutieux des jantes',
    useContain: false
  }
];

export const BeforeAfter = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const current = transformations[currentIndex];

  const handleMove = (e) => {
    if (!isDragging) return;
    
    const container = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - container.left;
    const percentage = (x / container.width) * 100;
    setSliderPosition(Math.min(Math.max(percentage, 0), 100));
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const container = e.currentTarget.getBoundingClientRect();
    const x = e.touches[0].clientX - container.left;
    const percentage = (x / container.width) * 100;
    setSliderPosition(Math.min(Math.max(percentage, 0), 100));
  };

  const nextTransformation = () => {
    setCurrentIndex((prev) => (prev + 1) % transformations.length);
    setSliderPosition(50);
  };

  const prevTransformation = () => {
    setCurrentIndex((prev) => (prev - 1 + transformations.length) % transformations.length);
    setSliderPosition(50);
  };

  return (
    <section id="avant-apres" className="py-24 bg-white scroll-mt-40">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h2 className="text-5xl font-bold mb-6 text-[#153254]">
              Avant/Après
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl">
              Découvrez le résultat de notre travail : des espaces transformés, 
              des clients satisfaits
            </p>
          </div>

          {/* Before/After Slider */}
          <div className="relative mb-8">
            <div
              className="relative w-full aspect-[16/9] overflow-hidden rounded-2xl shadow-2xl cursor-ew-resize select-none bg-gray-100"
              onMouseMove={handleMove}
              onMouseDown={() => setIsDragging(true)}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onTouchMove={handleTouchMove}
              onTouchStart={() => setIsDragging(true)}
              onTouchEnd={() => setIsDragging(false)}
            >
              {/* After Image (bottom layer) */}
              <img
                src={current.after}
                alt={`Après - ${current.title}`}
                className={`absolute inset-0 w-full h-full ${current.useContain ? 'object-contain' : 'object-cover'}`}
              />

              {/* Before Image (top layer with clip) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
              >
                <img
                  src={current.before}
                  alt={`Avant - ${current.title}`}
                  className={`absolute inset-0 w-full h-full ${current.useContain ? 'object-contain' : 'object-cover'}`}
                />
              </div>

              {/* Slider Line */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                style={{ left: `${sliderPosition}%` }}
              >
                {/* Slider Handle */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center cursor-ew-resize">
                  <div className="flex gap-1">
                    <ChevronLeft className="h-5 w-5 text-[#153254]" />
                    <ChevronRight className="h-5 w-5 text-[#153254]" />
                  </div>
                </div>
              </div>

              {/* Labels */}
              <div className="absolute top-4 left-4 bg-[#153254]/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-bold">
                Avant
              </div>
              <div className="absolute top-4 right-4 bg-[#D4AF37]/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-bold">
                Après
              </div>
            </div>

            {/* Navigation Arrows */}
            {transformations.length > 1 && (
              <>
                <button
                  onClick={prevTransformation}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 bg-white hover:bg-gray-100 text-[#153254] p-3 rounded-full shadow-xl transition-all"
                  aria-label="Transformation précédente"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={nextTransformation}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 bg-white hover:bg-gray-100 text-[#153254] p-3 rounded-full shadow-xl transition-all"
                  aria-label="Transformation suivante"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>

          {/* Description */}
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-2 text-[#153254]">
              {current.title}
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              {current.description}
            </p>

            {/* Dots Indicator */}
            {transformations.length > 1 && (
              <div className="flex justify-center gap-2">
                {transformations.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setSliderPosition(50);
                    }}
                    className={`h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-[#153254] w-12'
                        : 'bg-gray-300 w-2'
                    }`}
                    aria-label={`Voir transformation ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-700 mb-6">
              Envie de transformer votre espace ?
            </p>
            <Link to="/devis-premium">
              <button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white font-bold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all text-lg">
                Demander un devis gratuit
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
